import {
  Box,
  ButtonBase,
  Link as MUILink,
  Stack,
  SvgIconProps,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';

import { getClaims } from '../../api/claims/getClaims';
import { useGetPrimaryPolicyDocumentQuery } from '../../api/documents/getPrimaryPolicyDocument';
import { useWelcomeQuery } from '../../api/getWelcomeDetails';
import {
  PaperlessInfographic,
  WelcomeBill,
  WelcomeClipboard,
  WelcomeDocument,
  WelcomeShield,
} from '../../assets/icons/welcome';
import Button from '../../components/styledComponents/Button';
import colors from '../../constants/colors';
import useAnalytics from '../../hooks/useAnalytics';
import useUpdateTitle from '../../hooks/useUpdateTitle';
import { useSnackbarsContext } from '../../store/SnackbarProvider';
import theme from '../../styles/theme';
import {
  capitalizeName,
  deleteCookie,
  getCookie,
  getCustomerName,
} from '../../utils/helpers';
import SuccessfulConversion from '../RegistrationAndSignIn/Snackbars/SuccessfulConversion';

interface WelcomeCard {
  text: string;
  link?: string;
  href?: string;
  getIcon: () => any;
  dataTestId?: string;
}

const WelcomePage = () => {
  const { track } = useAnalytics();
  useUpdateTitle('MyPolicy');
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const queryClient = useQueryClient();
  const { enqueueSnackbar, snackbarVisible } = useSnackbarsContext();

  const { data, isLoading } = useWelcomeQuery();

  const prefetchOverviewData = () => {
    queryClient.prefetchQuery({
      queryKey: ['claims'],
      queryFn: () => getClaims(),
    });
  };

  const userJustConverted = useMemo(
    () => getCookie('convertedUser') === 'true',
    []
  );

  useEffect(() => {
    if (userJustConverted && data) {
      enqueueSnackbar({
        variant: 'info',
        dataTestId: 'conversion-info-banner',
        title: 'Your new password has been set.',
        content: <SuccessfulConversion />,
        key: 'conversion-success',
      });
    }
  }, [data, userJustConverted]);

  useEffect(
    () => () => {
      // Delete the DA redirect on welcome page unmount.
      // Our current BE Okta config always drops the users on welcome after sign in,
      // so this is the only place a user will ever land after login.
      localStorage.removeItem('mypolicyRedirectTo');

      // Also delete the conversion cookie, since this will only be set after an SSO user converts to DA
      deleteCookie('convertedUser');
    },
    []
  );

  useEffect(() => {
    if (!isLoading) {
      prefetchOverviewData();
    }
  }, [isLoading]);

  const hasAutoPolicy = useMemo(
    () => !!data?.policies?.find((policy) => policy.policyTypeCd === 'PA'),
    [data?.policies]
  );

  const filteredAutoPolicies =
    data?.policies.filter((policy) => policy?.policyTypeCd === 'PA') ?? [];

  const filteredPropertyPolicies =
    data?.policies.filter((policy) => policy?.policyTypeCd === 'HO') ?? [];

  const singleAutoPolicy = filteredAutoPolicies?.length === 1;
  const singlePropertyPolicy =
    data?.policies.length === 1 && filteredPropertyPolicies?.length === 1;

  const shouldUseExternalLink = singleAutoPolicy || singlePropertyPolicy;
  const getSinglePolicy = () => {
    if (singleAutoPolicy) {
      return filteredAutoPolicies[0];
    } else if (singlePropertyPolicy) {
      return filteredPropertyPolicies[0];
    }
    return { policyNumber: undefined, policyType: undefined };
  };

  const isNYPolicy = data?.policies.some((policy) => policy.riskState === 'NY');

  const {
    data: primaryPolicyDocumentDataUri,
    isLoading: policyDocumentLoading,
  } = useGetPrimaryPolicyDocumentQuery(
    getSinglePolicy().policyNumber,
    getSinglePolicy().policyType,
    shouldUseExternalLink
  );

  const getDocumentLink = () => {
    if (singleAutoPolicy || singlePropertyPolicy) {
      return primaryPolicyDocumentDataUri;
    }

    return `${window.location.origin}/documents`;
  };

  const cards: WelcomeCard[] = useMemo(
    () => [
      {
        text: 'Make a payment',
        link: '/bills',
        getIcon: () => WelcomeBill,
        dataTestId: 'make-payment-button-link',
      },
      {
        text: 'View policies',
        link: '/policies',
        getIcon: () => WelcomeShield,
        dataTestId: 'view-policies-button-link',
      },
      {
        text: 'View claims',
        link: '/claims',
        getIcon: () => WelcomeClipboard,
        dataTestId: 'view-claims-button-link',
      },
      {
        text: hasAutoPolicy
          ? 'Get insurance ID card'
          : 'Get proof of insurance',
        link: !shouldUseExternalLink || isNYPolicy ? '/documents' : undefined,
        ...(!isNYPolicy && shouldUseExternalLink
          ? { href: getDocumentLink() }
          : {}),
        getIcon: () => WelcomeDocument,
        dataTestId: 'id-card-button-link',
      },
    ],
    [hasAutoPolicy, primaryPolicyDocumentDataUri]
  );

  const cardRows = [cards.slice(0, 2), cards.slice(2)];

  const mypolicyRedirectTo = localStorage.getItem('mypolicyRedirectTo');

  if (mypolicyRedirectTo) {
    localStorage.removeItem('mypolicyRedirectTo');
    return <Navigate to={mypolicyRedirectTo} replace />;
  }

  return (
    <Stack
      data-testid="welcomePage"
      direction="column"
      sx={{
        pb: { mobile: 2, tablet: 3, desktop: 9 },
        pt: snackbarVisible
          ? { mobile: 1, tablet: 2, desktop: 3 }
          : { mobile: 2, tablet: 3, desktop: 9 },
        px: { mobile: 2, tablet: 4, desktop: 7 },
        width: 1,
      }}
    >
      <Typography
        variant="h1"
        className="csaa-qm-encrypt"
        sx={{
          fontSize: { mobile: '33px', tablet: '44px' },
          fontWeight: 500,
          lineHeight: { mobile: '48px', tablet: '56px' },
        }}
      >
        Welcome to MyPolicy, {capitalizeName(getCustomerName(data).firstName)}
      </Typography>
      <Typography
        sx={{
          fontSize: { mobile: '23px', tablet: '28px' },
          fontWeight: 500,
          lineHeight: { mobile: '32px', tablet: '40px' },
          mt: 0.5,
          color: colors.csaaDark,
        }}
      >
        How can we help you?
      </Typography>
      <Stack
        alignItems={{ mobile: 'flex-start', desktop: 'center' }}
        direction={{ mobile: 'column', tablet: 'column', desktop: 'row' }}
        spacing={{ mobile: 1, tablet: 2, desktop: 2.5 }}
        sx={{ my: 3 }}
      >
        {cardRows.map((cardRow, idx) => {
          return (
            <Stack
              direction="row"
              key={`row-${idx}`}
              spacing={{ mobile: 1, tablet: 2, desktop: 2.5 }}
              sx={{ width: 1 }}
            >
              {cardRow.map((card) => {
                const Icon = card.getIcon();
                const iconProps: SvgIconProps = isMobile
                  ? { height: 20, width: 20 }
                  : {};

                const handleClick = () => {
                  if (singleAutoPolicy) {
                    track('Document Downloaded', {
                      event_type: 'File Downloaded',
                      document_name: 'Policy Declaration',
                      location: 'Feature Card',
                      link: 'Policy Declaration',
                    });
                  } else if (singlePropertyPolicy) {
                    track('Document Downloaded', {
                      event_type: 'File Downloaded',
                      document_name: 'Identification Card',
                      location: 'Feature Card',
                      link: 'Identification Card',
                    });
                  }
                };

                return (
                  <ButtonBase
                    component={card.href ? MUILink : Link}
                    data-testid={card.dataTestId}
                    to={card.link}
                    href={card.href}
                    disabled={
                      Object.hasOwn(card, 'href')
                        ? shouldUseExternalLink && policyDocumentLoading
                        : false
                    }
                    disableRipple
                    key={card.text}
                    onClick={handleClick}
                    target={Object.hasOwn(card, 'href') ? '_blank' : undefined}
                    sx={{
                      width: 1,
                      '&:hover': {
                        textDecoration: 'none',
                        color: 'inherit',
                      },
                      '&:focus': {
                        borderRadius: 2,
                        outline: `solid 2px ${colors.csaaBlack}`,
                      },
                    }}
                  >
                    <Stack
                      direction={{ mobile: 'column', tablet: 'row' }}
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        backgroundColor: colors.csaaWhite,
                        p: { mobile: 1, tablet: 3 },
                        borderRadius: 2,
                        boxShadow: '0px 4px 8px 0px rgba(25, 30, 43, 0.08)',
                        width: 1,
                        height: { mobile: '104px', tablet: 'none' },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: { mobile: '16px', tablet: '18px' },
                          lineHeight: '24px',
                          fontWeight: 600,
                          textAlign: 'left',
                          width: 1,
                        }}
                      >
                        {card.text}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          borderRadius: 2,
                          backgroundColor: colors.csaaNeutralBackground,
                          p: 1,
                          ml: { mobile: 'auto', tablet: 3 },
                        }}
                      >
                        <Icon {...iconProps} />
                      </Box>
                    </Stack>
                  </ButtonBase>
                );
              })}
            </Stack>
          );
        })}
      </Stack>
      <Stack
        alignItems="center"
        data-testid="paperless-advertisement"
        direction={{ mobile: 'column', tablet: 'row' }}
        sx={{
          backgroundColor: colors.csaaWhite,
          p: { mobile: 2, tablet: 3 },
          borderRadius: 2,
          boxShadow: '0px 4px 8px 0px rgba(25, 30, 43, 0.08)',
        }}
        spacing={{ mobile: 2, tablet: 3 }}
      >
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            borderRadius: 2,
            backgroundColor: colors.csaaNeutralBackground,
            px: 5,
            py: 0.5,
            mr: { mobile: 0, tablet: 3 },
            width: { mobile: 1, tablet: 'min-content' },
          }}
        >
          <PaperlessInfographic />
        </Stack>
        <Stack direction="column" spacing={{ mobile: 1 }}>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              fontWeight: 700,
              fontSize: { mobile: '16px', tablet: '18px' },
              lineHeight: '24px',
            }}
          >
            Enroll in paperless
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              fontWeight: 400,
              fontSize: '16px',
            }}
          >
            By switching to electronic statements, our customers have saved 239
            tons of paper annually, equivalent to 5,730 trees.&#42;
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              fontWeight: 400,
              fontSize: { mobile: 10, tablet: 12 },
            }}
          >
            &#42;Numbers are approximate
          </Typography>
        </Stack>
        <Stack
          direction="row"
          justifyContent={{ mobile: 'flex-end' }}
          alignItems="center"
          sx={{
            ml: { tablet: 'auto !important' },
            width: { mobile: 1, tablet: 'min-content' },
          }}
        >
          <Button
            disableFocusRipple
            disableRipple
            component={Link}
            to="/preferences"
            onClick={() =>
              track('Communication Preferences Accessed', {
                event_type: 'Link Accessed',
                link: 'Go paperless',
                location: 'Go-Green Paperless Card',
              })
            }
            sx={{
              justifyContent: 'flex-end',
              color: colors.csaaSpectrumBlue50,
              whiteSpace: 'nowrap',
              px: 2,
            }}
            variant="text"
          >
            Set preferences
          </Button>
        </Stack>
      </Stack>
      <Stack
        alignItems="center"
        className="mcp-container-override"
        data-testid="mcp-advertisement"
        id="mcpContainer"
        direction={{ mobile: 'column', tablet: 'row' }}
        sx={{
          p: { mobile: 2, tablet: 3 },
          borderRadius: 2,
        }}
        spacing={{ mobile: 2, tablet: 0 }}
      />
    </Stack>
  );
};

export default WelcomePage;
